import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';
import type { Card } from '@/hooks/useCards';

export type CardPrint = Tables<'card_prints'>;
export type CollectionItem = Tables<'user_collection'> & {
  card?: Card;
  print?: CardPrint | null;
};

export function useCollection() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['collection', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('user_collection')
        .select('*, card:cards(*), print:card_prints(*)')
        .eq('user_id', user.id)
        .order('added_at', { ascending: false });
      if (error) throw error;
      return (data || []) as CollectionItem[];
    },
    enabled: !!user,
  });
}

/** Fetch all print variants (treatments) for a given card. */
export function useCardPrints(cardId: string | undefined) {
  return useQuery({
    queryKey: ['card-prints', cardId],
    enabled: !!cardId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('card_prints')
        .select('*')
        .eq('card_id', cardId!)
        .order('price_usd', { ascending: false, nullsFirst: false });
      if (error) throw error;
      return (data || []) as CardPrint[];
    },
  });
}

export function useAddToCollection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cardId,
      printId,
      photoUrl,
    }: { cardId: string; printId?: string | null; photoUrl?: string }) => {
      if (!user) throw new Error('Not authenticated');
      // Match on (user, card, print) — null print_id is treated as its own bucket
      let q = supabase
        .from('user_collection')
        .select('id, quantity')
        .eq('user_id', user.id)
        .eq('card_id', cardId);
      q = printId ? q.eq('print_id', printId) : q.is('print_id', null);
      const { data: existing } = await q.maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('user_collection')
          .update({ quantity: (existing.quantity || 1) + 1, ...(photoUrl ? { photo_url: photoUrl } : {}) })
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_collection')
          .insert({
            user_id: user.id,
            card_id: cardId,
            print_id: printId ?? null,
            quantity: 1,
            photo_url: photoUrl || null,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });
}

export function useUpdateCollectionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, quantity }: { id: string; quantity: number }) => {
      if (quantity <= 0) {
        const { error } = await supabase.from('user_collection').delete().eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_collection').update({ quantity }).eq('id', id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });
}
